import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { bundlesCatalogService } from '../../services/catalogService';
import {
  bundleLinkedPartnersService,
  bundleLifecycleService,
  bundleUpdatesService,
  bundleReferenceProjectsService,
} from '../../services/bundleProfileService';
import { useToast } from '../../contexts/ToastContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const getCategoryColor = (cat) => {
  const map = {
    security: 'bg-red-100 text-red-700',
    connectivity: 'bg-cyan-100 text-cyan-700',
    cloud: 'bg-purple-100 text-purple-700',
    enablement: 'bg-green-100 text-green-700',
    networking: 'bg-blue-100 text-blue-700',
    iot: 'bg-teal-100 text-teal-700',
    analytics: 'bg-orange-100 text-orange-700',
    general: 'bg-slate-100 text-slate-600',
  };
  return map?.[cat] || 'bg-slate-100 text-slate-600';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

const TIER_COLOR = {
  Strategic: 'bg-purple-100 text-purple-700',
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-slate-100 text-slate-600',
  Tactical: 'bg-blue-100 text-blue-700',
};

const LIFECYCLE_STAGES = ['draft', 'active', 'review', 'sunset'];

const STAGE_META = {
  draft: { label: 'Draft', icon: 'FileEdit', color: 'bg-slate-400', light: 'bg-slate-50 text-slate-600 border-slate-200' },
  active: { label: 'Active', icon: 'CheckCircle2', color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  review: { label: 'Under Review', icon: 'ClipboardList', color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  sunset: { label: 'Sunset', icon: 'Sunset', color: 'bg-slate-400', light: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const UPDATE_TYPE_META = {
  general: { label: 'General', color: 'bg-slate-100 text-slate-600', icon: 'Info' },
  composition_change: { label: 'Composition Change', color: 'bg-blue-100 text-blue-700', icon: 'Layers' },
  pricing_update: { label: 'Pricing Update', color: 'bg-amber-100 text-amber-700', icon: 'DollarSign' },
  eol_notice: { label: 'EOL Notice', color: 'bg-red-100 text-red-700', icon: 'AlertTriangle' },
};

const UPDATE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'composition_change', label: 'Composition Change' },
  { value: 'pricing_update', label: 'Pricing Update' },
  { value: 'eol_notice', label: 'EOL Notice' },
];

const SECTOR_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'government', label: 'Government' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
];

const SECTOR_COLOR = {
  government: 'bg-blue-100 text-blue-700',
  finance: 'bg-emerald-100 text-emerald-700',
  healthcare: 'bg-red-100 text-red-700',
  telecom: 'bg-purple-100 text-purple-700',
  retail: 'bg-orange-100 text-orange-700',
  manufacturing: 'bg-cyan-100 text-cyan-700',
  general: 'bg-slate-100 text-slate-600',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Post Update Modal ──────────────────────────────────────────────────────

function PostUpdateModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({ updateType: 'general', title: '', content: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form?.title?.trim()) return;
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-24 py-20 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Post Update</h3>
          <button onClick={onClose} className="p-6 rounded-lg hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="p-24 space-y-16">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Update Type</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              value={form?.updateType}
              onChange={e => setForm(p => ({ ...p, updateType: e?.target?.value }))}
            >
              {UPDATE_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Title *</label>
            <input
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              placeholder="Update title..."
              value={form?.title}
              onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Details</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
              rows={3}
              placeholder="Additional details..."
              value={form?.content}
              onChange={e => setForm(p => ({ ...p, content: e?.target?.value }))}
            />
          </div>
        </div>
        <div className="px-24 py-16 border-t border-slate-100 flex items-center justify-end gap-10">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="default" onClick={handleSubmit} disabled={saving || !form?.title?.trim()}>
            {saving ? 'Posting...' : 'Post Update'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Add Reference Project Modal ────────────────────────────────────────────

function AddProjectModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({ projectName: '', partnerName: '', sector: 'general', outcomeSummary: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form?.projectName?.trim()) return;
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-24 py-20 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Add Reference Project</h3>
          <button onClick={onClose} className="p-6 rounded-lg hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="p-24 space-y-16">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Project Name *</label>
            <input
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              placeholder="e.g. National Bank Security Overhaul"
              value={form?.projectName}
              onChange={e => setForm(p => ({ ...p, projectName: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Partner Name</label>
            <input
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              placeholder="Partner who delivered this project"
              value={form?.partnerName}
              onChange={e => setForm(p => ({ ...p, partnerName: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Sector</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              value={form?.sector}
              onChange={e => setForm(p => ({ ...p, sector: e?.target?.value }))}
            >
              {SECTOR_OPTIONS?.map(s => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Outcome Summary</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
              rows={3}
              placeholder="Describe the outcome and impact..."
              value={form?.outcomeSummary}
              onChange={e => setForm(p => ({ ...p, outcomeSummary: e?.target?.value }))}
            />
          </div>
        </div>
        <div className="px-24 py-16 border-t border-slate-100 flex items-center justify-end gap-10">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="default" onClick={handleSubmit} disabled={saving || !form?.projectName?.trim()}>
            {saving ? 'Adding...' : 'Add Project'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Add Lifecycle Stage Modal ──────────────────────────────────────────────

function AddLifecycleModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({ stage: 'active', stageDate: new Date()?.toISOString()?.split('T')?.[0], notes: '', expectedEolDate: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-24 py-20 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Record Stage Change</h3>
          <button onClick={onClose} className="p-6 rounded-lg hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="p-24 space-y-16">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Stage</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              value={form?.stage}
              onChange={e => setForm(p => ({ ...p, stage: e?.target?.value }))}
            >
              {LIFECYCLE_STAGES?.map(s => <option key={s} value={s}>{STAGE_META?.[s]?.label || s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Date</label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              value={form?.stageDate}
              onChange={e => setForm(p => ({ ...p, stageDate: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Notes</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
              rows={2}
              placeholder="Reason for stage change..."
              value={form?.notes}
              onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))}
            />
          </div>
          {form?.stage === 'sunset' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-6">Expected EOL Date</label>
              <input
                type="date"
                className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                value={form?.expectedEolDate}
                onChange={e => setForm(p => ({ ...p, expectedEolDate: e?.target?.value }))}
              />
            </div>
          )}
        </div>
        <div className="px-24 py-16 border-t border-slate-100 flex items-center justify-end gap-10">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="default" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Record Stage'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Bundle Profile Page ───────────────────────────────────────────────

export default function BundleProfile() {
  const { bundleId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('composition');
  const [compositionTab, setCompositionTab] = useState('products');

  const [linkedPartners, setLinkedPartners] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [refProjects, setRefProjects] = useState([]);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [unassignConfirm, setUnassignConfirm] = useState(null);

  const loadAll = useCallback(async () => {
    if (!bundleId) return;
    setLoading(true);
    try {
      const [bundleData, partners, lc, upd, projects] = await Promise.all([
        bundlesCatalogService?.getAll(),
        bundleLinkedPartnersService?.getByBundle(bundleId),
        bundleLifecycleService?.getByBundle(bundleId),
        bundleUpdatesService?.getByBundle(bundleId),
        bundleReferenceProjectsService?.getByBundle(bundleId),
      ]);
      const found = bundleData?.find(b => b?.id === bundleId);
      if (!found) { navigate('/bundles-catalog'); return; }
      setBundle(found);
      setLinkedPartners(partners);
      setLifecycle(lc);
      setUpdates(upd);
      setRefProjects(projects);
    } catch (err) {
      toast?.error('Failed to load bundle profile.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [bundleId, navigate, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUnassign = async (linkId) => {
    try {
      await bundleLinkedPartnersService?.unlink(linkId);
      setLinkedPartners(prev => prev?.filter(p => p?.id !== linkId));
      toast?.success('Partner unassigned from bundle.');
    } catch (err) {
      toast?.error('Failed to unassign partner.');
    } finally {
      setUnassignConfirm(null);
    }
  };

  const handlePostUpdate = async (formData) => {
    try {
      const created = await bundleUpdatesService?.create(bundleId, formData);
      setUpdates(prev => [created, ...prev]);
      toast?.success('Update posted.');
      setShowUpdateModal(false);
    } catch (err) {
      toast?.error('Failed to post update.');
    }
  };

  const handleAddProject = async (formData) => {
    try {
      const created = await bundleReferenceProjectsService?.create(bundleId, formData);
      setRefProjects(prev => [created, ...prev]);
      toast?.success('Reference project added.');
      setShowProjectModal(false);
    } catch (err) {
      toast?.error('Failed to add reference project.');
    }
  };

  const handleAddLifecycle = async (formData) => {
    try {
      await bundleLifecycleService?.addStage(bundleId, formData);
      const updated = await bundleLifecycleService?.getByBundle(bundleId);
      setLifecycle(updated);
      toast?.success('Stage recorded.');
      setShowLifecycleModal(false);
    } catch (err) {
      toast?.error('Failed to record stage.');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      await bundleUpdatesService?.delete(updateId);
      setUpdates(prev => prev?.filter(u => u?.id !== updateId));
      toast?.success('Update removed.');
    } catch (err) {
      toast?.error('Failed to remove update.');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await bundleReferenceProjectsService?.delete(projectId);
      setRefProjects(prev => prev?.filter(p => p?.id !== projectId));
      toast?.success('Reference project removed.');
    } catch (err) {
      toast?.error('Failed to remove project.');
    }
  };

  const currentStage = lifecycle?.length > 0 ? lifecycle?.[lifecycle?.length - 1]?.stage : null;

  const TABS = [
    { id: 'composition', label: 'Composition', icon: 'Layers' },
    { id: 'partners', label: `Partners (${linkedPartners?.length})`, icon: 'Users' },
    { id: 'usecases', label: 'Use Cases & Projects', icon: 'BookOpen' },
    { id: 'lifecycle', label: 'Lifecycle', icon: 'GitBranch' },
    { id: 'updates', label: `Updates (${updates?.length})`, icon: 'Bell' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/20 to-purple-50/20">
        <Header />
        <main className="pt-[148px] flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-16">
            <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Loading bundle profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!bundle) return null;

  const includedProductsList = bundle?.includedProducts ? bundle?.includedProducts?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [];
  const includedServicesList = bundle?.includedServices ? bundle?.includedServices?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [];

  return (
    <>
      <Helmet>
        <title>{bundle?.bundleName} — Bundle Profile | PartnerHub</title>
      </Helmet>
      {showUpdateModal && <PostUpdateModal onClose={() => setShowUpdateModal(false)} onSubmit={handlePostUpdate} />}
      {showProjectModal && <AddProjectModal onClose={() => setShowProjectModal(false)} onSubmit={handleAddProject} />}
      {showLifecycleModal && <AddLifecycleModal onClose={() => setShowLifecycleModal(false)} onSubmit={handleAddLifecycle} />}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/20 to-purple-50/20">
        <Header />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1400px] mx-auto">

            {/* Breadcrumb */}
            <div className="flex items-center gap-8 mb-24 text-sm text-slate-500">
              <Link to="/bundles-catalog" className="hover:text-violet-600 transition-colors flex items-center gap-6">
                <Icon name="Layers" size={14} />
                Bundles Catalog
              </Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-slate-900 font-medium">{bundle?.bundleName}</span>
            </div>

            {/* ── Bundle Header ─────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-24 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-32 py-28">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-20">
                  <div className="flex items-start gap-20">
                    <div className="p-14 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                      <Icon name="Layers" size={32} className="text-white" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-10 mb-8">
                        <h1 className="text-2xl font-bold text-white">{bundle?.bundleName}</h1>
                        {bundle?.bundleCategory && (
                          <span className="text-xs px-10 py-3 rounded-full bg-white/20 text-white font-medium capitalize">
                            {bundle?.bundleCategory}
                          </span>
                        )}
                        <span className={`text-xs px-10 py-3 rounded-full font-medium ${getStatusColor(bundle?.catalogStatus)}`}>
                          {bundle?.catalogStatus}
                        </span>
                      </div>
                      {bundle?.description && (
                        <p className="text-violet-100 text-sm max-w-2xl leading-relaxed">{bundle?.description}</p>
                      )}
                      {bundle?.targetMarket && (
                        <div className="flex items-center gap-6 mt-10">
                          <Icon name="Target" size={14} className="text-violet-200" />
                          <span className="text-violet-200 text-sm">{bundle?.targetMarket}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-10 flex-shrink-0">
                    <Link to={`/bundles-catalog`}>
                      <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" iconName="ArrowLeft" iconPosition="left" iconSize={16}>
                        Back
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
                {[
                  { label: 'Partners Offering', value: linkedPartners?.length, icon: 'Users', color: 'text-violet-600' },
                  { label: 'Included Products', value: includedProductsList?.length || '—', icon: 'Package', color: 'text-blue-600' },
                  { label: 'Included Services', value: includedServicesList?.length || '—', icon: 'Wrench', color: 'text-green-600' },
                  { label: 'Estimated Value', value: bundle?.estimatedValue || '—', icon: 'Banknote', color: 'text-amber-600' },
                ]?.map(stat => (
                  <div key={stat?.label} className="px-24 py-20">
                    <div className="flex items-center gap-8 mb-6">
                      <Icon name={stat?.icon} size={14} className={stat?.color} />
                      <span className="text-xs text-slate-500 font-medium">{stat?.label}</span>
                    </div>
                    <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Tab Navigation ─────────────────────────────────────────── */}
            <div className="flex items-center gap-4 mb-24 overflow-x-auto pb-4">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-8 px-16 py-10 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab?.id
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-violet-300 hover:text-violet-600'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* ── Tab Content ────────────────────────────────────────────── */}

            {/* Composition Tab */}
            {activeTab === 'composition' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-28 py-20 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-10">
                    <Icon name="Layers" size={18} className="text-violet-600" />
                    <span className="font-semibold text-slate-900">Bundle Composition</span>
                  </div>
                  <div className="flex items-center gap-4 bg-slate-100 rounded-lg p-4">
                    {[
                      { id: 'products', label: `Products (${includedProductsList?.length})`, icon: 'Package' },
                      { id: 'services', label: `Services (${includedServicesList?.length})`, icon: 'Wrench' },
                    ]?.map(t => (
                      <button
                        key={t?.id}
                        onClick={() => setCompositionTab(t?.id)}
                        className={`flex items-center gap-6 px-14 py-8 rounded-lg text-sm font-medium transition-all ${
                          compositionTab === t?.id ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Icon name={t?.icon} size={14} />
                        {t?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-28">
                  {compositionTab === 'products' && (
                    <>
                      {includedProductsList?.length === 0 ? (
                        <div className="text-center py-40">
                          <Icon name="Package" size={36} className="text-slate-300 mx-auto mb-12" />
                          <p className="text-slate-500 text-sm">No products listed for this bundle.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                          {includedProductsList?.map((product, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-xl border border-slate-200 p-16 flex items-start gap-12">
                              <div className="p-8 bg-blue-100 rounded-lg flex-shrink-0">
                                <Icon name="Package" size={16} className="text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{product}</p>
                                <span className="text-xs text-slate-500 mt-2 block">Product Component</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {compositionTab === 'services' && (
                    <>
                      {includedServicesList?.length === 0 ? (
                        <div className="text-center py-40">
                          <Icon name="Wrench" size={36} className="text-slate-300 mx-auto mb-12" />
                          <p className="text-slate-500 text-sm">No services listed for this bundle.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                          {includedServicesList?.map((service, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-xl border border-slate-200 p-16 flex items-start gap-12">
                              <div className="p-8 bg-green-100 rounded-lg flex-shrink-0">
                                <Icon name="Wrench" size={16} className="text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{service}</p>
                                <span className="text-xs text-slate-500 mt-2 block">Service Component</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Summary Bar */}
                  <div className="mt-24 pt-20 border-t border-slate-100 flex flex-wrap items-center gap-20">
                    <div className="flex items-center gap-8 text-sm text-slate-600">
                      <Icon name="Package" size={14} className="text-blue-500" />
                      <span><strong>{includedProductsList?.length}</strong> product{includedProductsList?.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-8 text-sm text-slate-600">
                      <Icon name="Wrench" size={14} className="text-green-500" />
                      <span><strong>{includedServicesList?.length}</strong> service{includedServicesList?.length !== 1 ? 's' : ''}</span>
                    </div>
                    {bundle?.estimatedValue && (
                      <div className="flex items-center gap-8 text-sm text-slate-600">
                        <Icon name="Banknote" size={14} className="text-amber-500" />
                        <span>Est. value: <strong>{bundle?.estimatedValue}</strong></span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Partners Tab */}
            {activeTab === 'partners' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-28 py-20 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-10">
                    <Icon name="Users" size={18} className="text-violet-600" />
                    <span className="font-semibold text-slate-900">Linked Partners</span>
                    <span className="text-xs bg-violet-100 text-violet-700 px-8 py-2 rounded-full font-medium">
                      {linkedPartners?.length}
                    </span>
                  </div>
                </div>

                {linkedPartners?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-64 gap-12">
                    <div className="p-20 bg-slate-100 rounded-full">
                      <Icon name="Users" size={36} className="text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">No partners are currently offering this bundle.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {linkedPartners?.map(partner => (
                      <div key={partner?.id}>
                        <div className="flex items-center gap-16 px-28 py-16 hover:bg-slate-50/60 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-10 mb-4">
                              <Link
                                to={`/partner-profile-portal/${partner?.partnerId}`}
                                className="font-semibold text-slate-900 hover:text-violet-600 transition-colors"
                              >
                                {partner?.partnerName}
                              </Link>
                              {partner?.partnerTier && (
                                <span className={`text-xs px-8 py-2 rounded-full font-medium ${TIER_COLOR?.[partner?.partnerTier] || 'bg-slate-100 text-slate-600'}`}>
                                  {partner?.partnerTier}
                                </span>
                              )}
                              {partner?.partnerStatus && (
                                <span className={`text-xs px-8 py-2 rounded-full font-medium ${getStatusColor(partner?.partnerStatus)}`}>
                                  {partner?.partnerStatus}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-16 text-xs text-slate-500">
                              {partner?.partnerCountry && (
                                <span className="flex items-center gap-4">
                                  <Icon name="MapPin" size={11} className="text-slate-400" />
                                  {partner?.partnerCountry}
                                </span>
                              )}
                              <span className="flex items-center gap-4">
                                <Icon name="Calendar" size={11} className="text-slate-400" />
                                Assigned {formatDate(partner?.createdAt)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-8 flex-shrink-0">
                            <Link to={`/partner-profile-portal/${partner?.partnerId}`}>
                              <button className="p-8 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors" title="View partner profile">
                                <Icon name="ExternalLink" size={15} />
                              </button>
                            </Link>
                            <button
                              onClick={() => setUnassignConfirm(partner?.id)}
                              className="p-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Unassign partner"
                            >
                              <Icon name="UserMinus" size={15} />
                            </button>
                          </div>
                        </div>
                        {unassignConfirm === partner?.id && (
                          <div className="px-28 py-14 bg-red-50 border-t border-red-100 flex items-center justify-between">
                            <div className="flex items-center gap-10">
                              <Icon name="AlertTriangle" size={15} className="text-red-500" />
                              <span className="text-sm text-red-700 font-medium">
                                Unassign <strong>{partner?.partnerName}</strong> from this bundle?
                              </span>
                            </div>
                            <div className="flex items-center gap-10">
                              <Button variant="outline" size="sm" onClick={() => setUnassignConfirm(null)}>Cancel</Button>
                              <Button variant="destructive" size="sm" onClick={() => handleUnassign(partner?.id)}>Unassign</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Use Cases & Reference Projects Tab */}
            {activeTab === 'usecases' && (
              <div className="space-y-24">
                {/* Use Cases from bundle data */}
                {bundle?.referenceUseCases && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28">
                    <div className="flex items-center gap-10 mb-20">
                      <Icon name="Lightbulb" size={18} className="text-amber-500" />
                      <span className="font-semibold text-slate-900">Use Cases</span>
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">{bundle?.referenceUseCases}</p>
                  </div>
                )}

                {/* Reference Projects */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-28 py-20 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-10">
                      <Icon name="BookOpen" size={18} className="text-violet-600" />
                      <span className="font-semibold text-slate-900">Reference Projects</span>
                      <span className="text-xs bg-violet-100 text-violet-700 px-8 py-2 rounded-full font-medium">
                        {refProjects?.length}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      iconSize={14}
                      onClick={() => setShowProjectModal(true)}
                    >
                      Add Project
                    </Button>
                  </div>

                  {refProjects?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-48 gap-12">
                      <Icon name="BookOpen" size={36} className="text-slate-300" />
                      <p className="text-slate-500 text-sm">No reference projects yet.</p>
                      <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" iconSize={14} onClick={() => setShowProjectModal(true)}>
                        Add First Project
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {refProjects?.map(project => (
                        <div key={project?.id} className="px-28 py-20 flex items-start gap-16 hover:bg-slate-50/60 transition-colors group">
                          <div className="p-10 bg-violet-100 rounded-xl flex-shrink-0">
                            <Icon name="FolderOpen" size={18} className="text-violet-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-10 mb-6">
                              <span className="font-semibold text-slate-900">{project?.projectName}</span>
                              {project?.sector && project?.sector !== 'general' && (
                                <span className={`text-xs px-8 py-2 rounded-full font-medium capitalize ${SECTOR_COLOR?.[project?.sector] || 'bg-slate-100 text-slate-600'}`}>
                                  {project?.sector}
                                </span>
                              )}
                            </div>
                            {project?.partnerName && (
                              <p className="text-sm text-slate-500 mb-6 flex items-center gap-6">
                                <Icon name="Building2" size={12} className="text-slate-400" />
                                {project?.partnerName}
                              </p>
                            )}
                            {project?.outcomeSummary && (
                              <p className="text-sm text-slate-600 leading-relaxed">{project?.outcomeSummary}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteProject(project?.id)}
                            className="p-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove project"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lifecycle Tab */}
            {activeTab === 'lifecycle' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-28 py-20 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-10">
                    <Icon name="GitBranch" size={18} className="text-violet-600" />
                    <span className="font-semibold text-slate-900">Bundle Lifecycle</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={14}
                    onClick={() => setShowLifecycleModal(true)}
                  >
                    Record Stage
                  </Button>
                </div>

                <div className="p-28">
                  {/* Stage Progress Bar */}
                  <div className="flex items-center gap-0 mb-32 overflow-x-auto pb-4">
                    {LIFECYCLE_STAGES?.map((stage, idx) => {
                      const meta = STAGE_META?.[stage];
                      const isReached = lifecycle?.some(l => l?.stage === stage);
                      const isCurrent = currentStage === stage;
                      return (
                        <React.Fragment key={stage}>
                          <div className={`flex flex-col items-center gap-8 px-16 py-12 rounded-xl border-2 transition-all min-w-[120px] ${
                            isCurrent ? `${meta?.light} border-current shadow-sm` :
                            isReached ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white border-slate-100 text-slate-400'
                          }`}>
                            <div className={`w-8 h-8 rounded-full ${isCurrent ? meta?.color : isReached ? 'bg-slate-400' : 'bg-slate-200'}`} />
                            <Icon name={meta?.icon} size={18} />
                            <span className="text-xs font-semibold text-center">{meta?.label}</span>
                          </div>
                          {idx < LIFECYCLE_STAGES?.length - 1 && (
                            <div className={`h-0.5 flex-1 min-w-[24px] ${isReached ? 'bg-slate-300' : 'bg-slate-100'}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Timeline */}
                  {lifecycle?.length === 0 ? (
                    <div className="text-center py-32">
                      <Icon name="GitBranch" size={32} className="text-slate-300 mx-auto mb-12" />
                      <p className="text-slate-500 text-sm">No lifecycle stages recorded yet.</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-slate-100" />
                      <div className="space-y-0">
                        {lifecycle?.map((entry, idx) => {
                          const meta = STAGE_META?.[entry?.stage] || STAGE_META?.draft;
                          return (
                            <div key={entry?.id} className="flex items-start gap-16 pb-24 relative">
                              <div className={`w-10 h-10 rounded-full ${meta?.color} flex-shrink-0 mt-4 z-10 ring-2 ring-white`} />
                              <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-16">
                                <div className="flex items-center justify-between mb-6">
                                  <span className={`text-xs font-bold px-8 py-3 rounded-full border ${meta?.light}`}>
                                    {meta?.label}
                                  </span>
                                  <span className="text-xs text-slate-400">{formatDate(entry?.stageDate)}</span>
                                </div>
                                {entry?.notes && <p className="text-sm text-slate-600 mb-6">{entry?.notes}</p>}
                                <div className="flex flex-wrap gap-16 text-xs text-slate-400">
                                  <span className="flex items-center gap-4">
                                    <Icon name="User" size={11} />
                                    {entry?.changedByName}
                                  </span>
                                  {entry?.expectedEolDate && (
                                    <span className="flex items-center gap-4 text-amber-600">
                                      <Icon name="Calendar" size={11} />
                                      EOL: {formatDate(entry?.expectedEolDate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Updates Feed Tab */}
            {activeTab === 'updates' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-28 py-20 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-10">
                    <Icon name="Bell" size={18} className="text-violet-600" />
                    <span className="font-semibold text-slate-900">Updates & News Feed</span>
                    <span className="text-xs bg-violet-100 text-violet-700 px-8 py-2 rounded-full font-medium">
                      {updates?.length}
                    </span>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={14}
                    onClick={() => setShowUpdateModal(true)}
                  >
                    Post Update
                  </Button>
                </div>

                {updates?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-64 gap-12">
                    <div className="p-20 bg-slate-100 rounded-full">
                      <Icon name="Bell" size={36} className="text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">No updates posted yet.</p>
                    <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" iconSize={14} onClick={() => setShowUpdateModal(true)}>
                      Post First Update
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {updates?.map(update => {
                      const meta = UPDATE_TYPE_META?.[update?.updateType] || UPDATE_TYPE_META?.general;
                      return (
                        <div key={update?.id} className="px-28 py-20 flex items-start gap-16 hover:bg-slate-50/60 transition-colors group">
                          <div className={`p-8 rounded-xl flex-shrink-0 ${meta?.color}`}>
                            <Icon name={meta?.icon} size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-10 mb-6">
                              <span className="font-semibold text-slate-900">{update?.title}</span>
                              <span className={`text-xs px-8 py-2 rounded-full font-medium ${meta?.color}`}>
                                {meta?.label}
                              </span>
                            </div>
                            {update?.content && (
                              <p className="text-sm text-slate-600 leading-relaxed mb-8">{update?.content}</p>
                            )}
                            <div className="flex items-center gap-16 text-xs text-slate-400">
                              <span className="flex items-center gap-4">
                                <Icon name="User" size={11} />
                                {update?.authorName}
                              </span>
                              <span className="flex items-center gap-4">
                                <Icon name="Clock" size={11} />
                                {formatDate(update?.createdAt)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteUpdate(update?.id)}
                            className="p-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove update"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
    </>
  );
}
